import { useEffect, useState, useCallback, useRef } from "react";
import { Button, message } from "antd";
import {
  PhoneOutlined,
  CloseCircleOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined
} from "@ant-design/icons";
import MinimizeIcon from "./assets/minimize.svg";
import MaximizeIcon from "./assets/maximize.svg";
import styles from "./App.module.css";
import VideoCall from "./video_call/VideoCallPage";
import { useToken } from "./context/TokenContext";
import { declineCall } from "./data/api";
import { DeclineCallReasonsEnum, RejectionReasonsEnum } from "./utils/enums";
import { useRingtone } from "./hooks/useRingtone";
import skypeRingtone from "./assets/skype.mp3";
import YesIcon from "./assets/yes.svg";
import NoIcon from "./assets/no.svg";
import RejectReasonForm from "./rejection_reason/RejectReasonForm";
import {
  addDeclinedCallInStorage,
  getUserDeclinedCalls,
  removeDeclinedCallFromStorage
} from "./utils/autoDeclinedCalls";

//TODO: remove below line
// eslint-disable-next-line no-unused-vars
export default function App({ options }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [callData, setCallData] = useState(null);
  const [incomingCallData, setIncomingCallData] = useState(null);
  const [showButtons, setShowButtons] = useState(false);
  const [countdown, setCountdown] = useState(20);
  const [messageApi, contextHolder] = message.useMessage();

  const { setToken, token } = useToken();
  const { play: playRingtone, stop: stopRingtone } = useRingtone(skypeRingtone);

  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectionSource, setRejectionSource] = useState("");
  const [isDeclining, setIsDeclining] = useState(false);
  const declineHandledRef = useRef(false);
  const [webRoomUrl, setSetWebRoomUrl] = useState("");

  const handleIncoming = (e) => {
    declineHandledRef.current = false;
    setShowRejectForm(false);
    playRingtone();

    console.log("handle incoming", e.detail.webRoomUrl);
    setSetWebRoomUrl(e.detail.webRoomUrl);
    const urlParams = new URL(e.detail.webRoomUrl).searchParams;
    const newToken = urlParams.get("t");
    setToken(newToken);

    const newCallData = {
      callToken: urlParams.get("videoSDKJWT") || "",
      sessionName: urlParams.get("sessionName") || "",
      sessionPasscode: urlParams.get("sessionPasscode") || "",
      userName: urlParams.get("userName") || "",
      callId: urlParams.get("callId") || "",
      userId: urlParams.get("i") || "",
      role: urlParams.get("g") || "",
      isPreviewEnabled: urlParams.get("p") === "1",
      language: urlParams.get("ln") || "",
      location: urlParams.get("lc") || "",
      phoneNumber: urlParams.get("pn") || "",
      appName: urlParams.get("an") || "",
      providerName: urlParams.get("prn") || "",
      popUpDisplayName: urlParams.get("popUpDisplayName") || "",
      isInvitationCall: urlParams.get("isInvitationCall") === "true"
    };

    setIncomingCallData(newCallData);
  };

  useEffect(() => {
    const autoDeclinedCalls = getUserDeclinedCalls();
    if (token && autoDeclinedCalls.length > 0) {
      const { callId, reason } = autoDeclinedCalls[0];
      declineHandledRef.current = false;
      const previousMissedCallData = {
        callId
      };
      setIncomingCallData(previousMissedCallData);
      setRejectionSource(reason);
      setShowRejectForm(true);
    }
  }, [token]);

  const handleDeclineCall = useCallback(
    async (reasonSource) => {
      if (!incomingCallData || declineHandledRef.current) return;

      declineHandledRef.current = true;
      setIsDeclining(true);

      addDeclinedCallInStorage(incomingCallData.callId, reasonSource);
      setRejectionSource(reasonSource);

      try {
        const { success, message: msg } = await declineCall(
          incomingCallData.callId,
          incomingCallData.userId,
          reasonSource === RejectionReasonsEnum.CANCELLED_CALL
            ? DeclineCallReasonsEnum.CANCELLED_CALL
            : DeclineCallReasonsEnum.CALL_MISSED,
          token
        );

        if (success) {
          messageApi.success(msg);
        } else {
          messageApi.error(msg);
        }

        stopRingtone();
        setShowRejectForm(true);
      } catch (error) {
        stopRingtone();
        const errMsg =
          error instanceof Error ? error.message : "Something went wrong";
        console.log("Failed to decline call:", errMsg);
        messageApi.error(errMsg);
        setShowRejectForm(true);
      } finally {
        // Remove prevent close listener
        if (window.TruDocVideo?.removePreventCloseListener) {
          window.TruDocVideo.removePreventCloseListener();
        }
        // Clear timeouts on EMR for declined calls
        if (window.TruDocVideo?.clearTimeouts) {
          console.log(
            "Calling clearTimeouts for provider:",
            incomingCallData.providerName
          );
          window.TruDocVideo.clearTimeouts({
            provider: "zoom"
          });
        }
        setIsDeclining(false);
      }
    },
    [incomingCallData, token]
  );
  // const handleDeclineCall = useCallback(
  //   (reasonSource = "auto") => {
  //     if (!incomingCallData) return;

  //     setShowRejectForm(true); // Show the reject reason form

  //     stopRingtone();
  //   },
  //   [incomingCallData]
  // );

  // const handleAnswerCall = () => {
  //   setCallData(incomingCallData);
  //   setIncomingCallData(null);
  //   setIsExpanded(true);
  //   setShowRejectForm(false);
  //   setRejectionSource("");
  //   stopRingtone();
  //   window.TruDocVideo.postAnswerCallFunction(); //call EMR function to do pre populate profile on EMR when accepting call
  // };
  const handleAnswerCall = () => {
    const screenWidth = window.screen.availWidth;
    const screenHeight = window.screen.availHeight;
    const popupWidth = 700;
    const popupHeight = 500;

    const left = screenWidth - popupWidth - 20; 
    const top = screenHeight - popupHeight - 60;

    //make it right bottom
    window.open(
      webRoomUrl,
      "_blank",
      `toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes,width=${popupWidth},height=${popupHeight},left=${left},top=${top}`
    );

    // Clear the incoming call state
    setIncomingCallData(null);
    setShowRejectForm(false);
    setRejectionSource("");
    stopRingtone();
    window.TruDocVideo.postAnswerCallFunction(); //call EMR function to do pre populate profile on EMR when accepting call
  };

  const handleLogout = () => {
    console.log("Logout--->");
    setCallData(null);
    setIsExpanded(false);
    setIncomingCallData(null);
    setShowRejectForm(false);
    stopRingtone();
  };

  useEffect(() => {
    if (incomingCallData && !callData && !showRejectForm) {
      setShowButtons(false);
      setCountdown(20);

      const showTimer = setTimeout(() => setShowButtons(true), 2000);

      const interval = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            handleDeclineCall(RejectionReasonsEnum.CALL_MISSED);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => {
        clearTimeout(showTimer);
        clearInterval(interval);
      };
    }
  }, [incomingCallData, callData, handleDeclineCall, showRejectForm]);

  useEffect(() => {
    const handleStart = (e) => {
      playRingtone();
      setShowRejectForm(false);
      const urlParams = new URL(e.detail.webRoomUrl).searchParams;
      setToken(urlParams.get("t"));

      const newCallData = {
        callToken: urlParams.get("videoSDKJWT") || "",
        sessionName: urlParams.get("sessionName") || "",
        sessionPasscode: urlParams.get("sessionPasscode") || "",
        userName: urlParams.get("userName") || "",
        callId: urlParams.get("callId") || "",
        userId: urlParams.get("i") || "",
        role: urlParams.get("g") || "",
        isPreviewEnabled: urlParams.get("p") === "1"
      };
      setCallData(newCallData);
      setIsExpanded(true);
    };

    const handleEnd = () => {
      console.log("Received end-call");
      setCallData(null);
      setIsExpanded(false);
      window.location.reload();
      stopRingtone();
      if (window.TruDocVideo?.removePreventCloseListener) {
        window.TruDocVideo.removePreventCloseListener();
      }
    };

    const handlePatientCancelledCall = () => {
      // Check if there are any rejections for the current token/user
      const autoDeclinedCalls = getUserDeclinedCalls();
      if (autoDeclinedCalls.length > 0) {
        const { callId, reason } = autoDeclinedCalls[0];
        declineHandledRef.current = false;
        const previousMissedCallData = {
          callId
        };
        setIncomingCallData(previousMissedCallData);
        setRejectionSource(reason);
        setShowRejectForm(true);
      } else {
        setIncomingCallData(null);
      }
      stopRingtone();
    };

    window.addEventListener("start-call", handleStart);
    window.addEventListener("end-call", handleEnd);
    window.addEventListener("incoming-call", handleIncoming);
    window.addEventListener("logout-video-call", handleLogout);
    window.addEventListener(
      "patient-cancelled-call",
      handlePatientCancelledCall
    );

    return () => {
      window.removeEventListener("start-call", handleStart);
      window.removeEventListener("end-call", handleEnd);
      window.removeEventListener("incoming-call", handleIncoming);
      window.removeEventListener("logout-video-call", handleLogout);
      window.removeEventListener(
        "patient-cancelled-call",
        handlePatientCancelledCall
      );
    };
  }, []);

  const toggleExpand = () => {
    const nextState = !isExpanded;
    setIsExpanded(nextState);

    setTimeout(() => {
      const shadowHost = document.getElementById("trudoc-video-widget");
      if (!shadowHost || !shadowHost.shadowRoot) {
        console.warn("Shadow root not found on #trudoc-video-widget");
        return;
      }

      const shadowRoot = shadowHost.shadowRoot;

      const targetEl = Array.from(shadowRoot.querySelectorAll("main")).find(
        () => true
      );
      const footerTargetEl = Array.from(
        shadowRoot.querySelectorAll("footer")
      ).find(() => true);

      const chatButton = shadowRoot.getElementById("uikit-footer-chat-button");
      const moreButton = shadowRoot.getElementById("footer-more-button");

      if (!targetEl || !footerTargetEl) return;

      if (!nextState) {
        targetEl.style.width = "330px";
        targetEl.style.height = "350px";
        footerTargetEl.style.width = "100%";
        footerTargetEl.style.position = "absolute";
        footerTargetEl.style.bottom = "0";
        if (chatButton) chatButton.style.display = "none";
        if (moreButton) moreButton.style.display = "none";
      } else {
        targetEl.style.width = "832px";
        targetEl.style.height = "879px";
        footerTargetEl.style.maxWidth = "100%";
        footerTargetEl.style.width = "100%";
        if (chatButton) chatButton.style.display = "";
        if (moreButton) moreButton.style.display = "";
        void targetEl.offsetHeight;
        window.dispatchEvent(new Event("resize"));
      }
    }, 200);
  };

  return (
    <>
      {contextHolder}

      {(incomingCallData || callData) && (
        <div
          className={
            incomingCallData && !callData && !showRejectForm
              ? `${styles.widgetContainerCentered}`
              : `${styles.widgetContainer} ${isExpanded ? styles.expanded : ""}`
          }
        >
          {incomingCallData && !callData && !showRejectForm && (
            <div className={styles.incomingCallOverlay}>
              <div className={styles.incomingCallBox}>
                <p className={styles.callerName}>
                  {incomingCallData.isInvitationCall
                    ? "Invitation Call"
                    : "Incoming Call"}
                </p>
                <br />
                <p className={styles.callerName}>
                  {incomingCallData.popUpDisplayName}
                </p>
                <div style={{ marginTop: "10px" }} />
                <p className={styles.callerInfo}>
                  {incomingCallData.phoneNumber}
                </p>

                <div className={styles.chipRow}>
                  {incomingCallData.language && (
                    <div className={styles.chipContainer}>
                      <p className={styles.chipText}>
                        {incomingCallData.language}
                      </p>
                    </div>
                  )}
                  {incomingCallData.location && (
                    <div className={styles.chipContainer}>
                      <p className={styles.chipText}>
                        {incomingCallData.location}
                      </p>
                    </div>
                  )}
                  {incomingCallData.appName && (
                    <div className={styles.chipContainer}>
                      <p className={styles.chipText}>
                        {incomingCallData.appName}
                      </p>
                    </div>
                  )}
                  {incomingCallData.providerName && (
                    <div className={styles.chipContainer}>
                      <p className={styles.chipText}>
                        {incomingCallData.providerName}
                      </p>
                    </div>
                  )}
                </div>

                {showButtons ? (
                  <>
                    <div className={styles.buttonGroup}>
                      <img
                        src={YesIcon}
                        alt="Answer"
                        onClick={isDeclining ? null : handleAnswerCall}
                        style={{
                          cursor: isDeclining ? "not-allowed" : "pointer",
                          width: 40,
                          height: 40,
                          marginRight: 16,
                          opacity: isDeclining ? 0.5 : 1
                        }}
                        disabled={isDeclining}
                      />
                      <img
                        src={NoIcon}
                        alt="Decline"
                        onClick={() =>
                          !isDeclining &&
                          handleDeclineCall(RejectionReasonsEnum.CANCELLED_CALL)
                        }
                        style={{
                          cursor: isDeclining ? "not-allowed" : "pointer",
                          width: 40,
                          height: 40,
                          opacity: isDeclining ? 0.5 : 1
                        }}
                        disabled={isDeclining}
                      />
                    </div>
                    <div style={{ marginTop: "10px" }} />
                    {isDeclining ? (
                      <div
                        className={styles.loader}
                        style={{ textAlign: "center" }}
                      >
                        <span>Declining call...</span>
                      </div>
                    ) : (
                      <p className={styles.autoDeclineTimer}>
                        Auto-declining in 00:{countdown}s
                      </p>
                    )}
                  </>
                ) : (
                  <p
                    style={{
                      marginTop: "2rem",
                      fontSize: "1rem",
                      color: "#aaa"
                    }}
                  >
                    Preparing call...
                  </p>
                )}
              </div>
            </div>
          )}
          {callData && (
            <>
              <Button
                onClick={toggleExpand}
                style={{
                  position: "absolute",
                  top: "10px",
                  right: "10px",
                  zIndex: 10
                }}
                shape="circle"
                icon={
                  <img
                    src={isExpanded ? MinimizeIcon : MaximizeIcon}
                    alt={isExpanded ? "Minimize" : "Maximize"}
                    title={isExpanded ? "Minimize" : "Maximize"}
                    style={{ width: 23, height: 23 }}
                  />
                }
              />
              <VideoCall
                callToken={callData.callToken}
                sessionName={callData.sessionName}
                sessionPasscode={callData.sessionPasscode}
                userName={callData.userName}
                callId={callData.callId}
                userId={callData.userId}
                role={callData.role}
                isPreviewEnabled={callData.isPreviewEnabled}
                isPipModeEnabled={isExpanded}
              />
            </>
          )}
          {showRejectForm && !callData && (
            <div className={styles.incomingCallOverlay}>
              <RejectReasonForm
                callId={incomingCallData.callId}
                token={token}
                rejectionSource={rejectionSource}
                onSuccess={async () => {
                  removeDeclinedCallFromStorage(incomingCallData.callId);
                  setCallData(null);
                  setIncomingCallData(null);
                  setIsExpanded(false);
                  setShowRejectForm(false);
                  stopRingtone();
                }}
              />
            </div>
          )}
        </div>
      )}
    </>
  );
}
